import { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CalendarDays, ChevronDown, ChevronUp, Home, Plane, Info } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ClubCrest } from "@/components/ClubCrest";

interface UpcomingFixture {
  fixtureId: number;
  date: string;
  kickoffTime: string | null;
  isHome: boolean;
  opponentName: string;
  opponentLogo: string;
  leagueCode: string;
  leagueName: string;
  venueName: string | null;
}

interface UpcomingFixturesProps {
  clubId: string;
  clubName: string;
  leagueCode: string;
}

const INITIAL_COUNT = 5;

const UpcomingFixtures = ({ clubId, clubName, leagueCode }: UpcomingFixturesProps) => {
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['upcoming-fixtures', clubId, leagueCode],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('fetch-upcoming-fixtures', {
        body: { clubId, leagueCode },
      });
      if (error) throw error;
      return data as { fixtures: UpcomingFixture[]; clubName: string };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const fixtures = data?.fixtures || [];
  const visibleFixtures = showAll ? fixtures : fixtures.slice(0, INITIAL_COUNT);
  const hasMore = fixtures.length > INITIAL_COUNT;

  if (error) return null; // Silently fail — fixtures are supplementary

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-muted-foreground" />
          Upcoming Fixtures
        </CardTitle>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Info className="h-3 w-3" />
          League fixtures only — cup and continental matches are not included.
        </p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : fixtures.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No upcoming fixtures scheduled.
          </p>
        ) : (
          <>
            <div className="divide-y divide-border">
              {visibleFixtures.map((fixture) => (
                <div
                  key={fixture.fixtureId}
                  className="flex items-center gap-3 py-2.5 text-sm"
                >
                  {/* Date */}
                  <span className="text-muted-foreground w-[70px] shrink-0 text-xs">
                    {fixture.date
                      ? format(new Date(fixture.date), "d MMM")
                      : "TBD"}
                  </span>

                  {/* Home/Away badge */}
                  <Badge
                    variant={fixture.isHome ? "default" : "outline"}
                    className="w-9 justify-center text-[10px] px-1 py-0"
                  >
                    {fixture.isHome ? (
                      <span className="flex items-center gap-0.5"><Home className="h-3 w-3" /> H</span>
                    ) : (
                      <span className="flex items-center gap-0.5"><Plane className="h-3 w-3" /> A</span>
                    )}
                  </Badge>

                  {/* Opponent */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <ClubCrest clubName={fixture.opponentName} size="sm" />
                    <span className="truncate font-medium">
                      {fixture.isHome ? "vs" : "@"} {fixture.opponentName}
                    </span>
                  </div>

                  {/* Kickoff time */}
                  <span className="text-muted-foreground text-xs shrink-0">
                    {fixture.kickoffTime
                      ? format(new Date(fixture.kickoffTime), "HH:mm")
                      : "TBD"}
                  </span>
                </div>
              ))}
            </div>

            {hasMore && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-2"
                onClick={() => setShowAll(!showAll)}
              >
                {showAll ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    Show fewer
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Show all {fixtures.length} fixtures
                  </>
                )}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default UpcomingFixtures;
